import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { EyeIcon, EyeOffIcon, Loader2, XIcon } from 'lucide-react';
import apiClient from '../../utils/apiClient';

const SettingsModal = ({ isOpen, onClose }) => {
    const [activeTab, setActiveTab] = useState('password');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [otpTime, setOtpTime] = useState(60);
    const [isResendActive, setIsResendActive] = useState(false);
    const [resentOtp, setResentOtp] = useState(false);
    const otpTimerRef = useRef(null);

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        // Validate current password field
        if (!currentPassword) {
            setError('Please enter your current password');
            setIsLoading(false);
            return;
        }

        // Password validation
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;
        
        if (!passwordRegex.test(newPassword)) {
            setError('New password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character');
            setIsLoading(false);
            return;
        }

        // Confirm password validation
        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            setIsLoading(false);
            return;
        }

        try {
            const response = await apiClient.post('/auth/change-password', {
                currentPassword,
                newPassword
            });

            if (response.status === 200) {
                onClose();
                alert('Password changed successfully!');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to change password');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailVerification = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            // Generate and send OTP for new email verification
            const otp = Math.floor(100000 + Math.random() * 900000);
            const expirationTime = new Date(Date.now() + 7 * 60 * 1000); // 7 minutes

            const response = await apiClient.post('/auth/verify-email', {
                newEmail,
                otp,
                otpExpiredAt: expirationTime
            });

            if (response.status === 200) {
                setShowOtpInput(true);
                startTimer(); // Add this function to manage OTP timer
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to send verification code');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailChange = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const response = await apiClient.post('/auth/change-email', {
                newEmail,
                otp
            });

            if (response.status === 200) {
                onClose();
                // Dispatch an event to update the UI
                window.dispatchEvent(new Event('email-changed'));
                alert('Email changed successfully!');
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to change email');
        } finally {
            setIsLoading(false);
        }
    };

    // Add timer functionality
    const startTimer = () => {
        setOtpTime(60);
        setIsResendActive(false);
        setResentOtp(false);
        clearInterval(otpTimerRef.current);

        otpTimerRef.current = setInterval(() => {
            setOtpTime((prev) => {
                if (prev <= 1) {
                    clearInterval(otpTimerRef.current);
                    setIsResendActive(true);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const formatTime = (seconds) => {
        const min = String(Math.floor(seconds / 60)).padStart(2, '0');
        const sec = String(seconds % 60).padStart(2, '0');
        return `${min}:${sec}`;
    };

    // Add cleanup for timer
    useEffect(() => {
        return () => clearInterval(otpTimerRef.current);
    }, []);

    const handleResendOtp = async () => {
        if (!resentOtp && isResendActive) {
            setResentOtp(true);
            setIsResendActive(false);
            setIsLoading(true);

            try {
                await handleEmailVerification();
            } catch (error) {
                setError('Failed to resend OTP');
            } finally {
                setIsLoading(false);
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-400 px-6 py-4">
                    <div className="flex justify-between items-center">
                        <CardTitle className="text-xl font-semibold text-white">
                            Settings
                        </CardTitle>
                        <button 
                            onClick={onClose}
                            className="p-1 rounded-full hover:bg-white/20 transition-colors"
                        >
                            <XIcon className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </CardHeader>
                
                <CardContent className="p-0">
                    {/* Tab Headers */}
                    <div className="flex bg-gray-50 border-b">
                        <button 
                            className={`flex-1 py-3 px-4 text-center border-b-2 font-medium transition-colors ${
                                activeTab === 'password' 
                                    ? 'border-blue-500 bg-white text-blue-600' 
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                            onClick={() => setActiveTab('password')}
                        >
                            <div className="flex items-center justify-center gap-2">
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                    activeTab === 'password' 
                                        ? 'bg-blue-500' 
                                        : 'border border-gray-300'
                                }`}>
                                    {activeTab === 'password' && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                </div>
                                Password
                            </div>
                        </button>
                        <button 
                            className={`flex-1 py-3 px-4 text-center border-b-2 font-medium transition-colors ${
                                activeTab === 'email' 
                                    ? 'border-blue-500 bg-white text-blue-600' 
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}
                            onClick={() => setActiveTab('email')}
                        >
                            <div className="flex items-center justify-center gap-2">
                                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                    activeTab === 'email' 
                                        ? 'bg-blue-500' 
                                        : 'border border-gray-300'
                                }`}>
                                    {activeTab === 'email' && <div className="w-2 h-2 bg-white rounded-full"></div>}
                                </div>
                                Email
                            </div>
                        </button>
                    </div>

                    {/* Form Content */}
                    <div className="p-6">
                        {activeTab === 'password' && (
                            <form onSubmit={handlePasswordChange} className="space-y-5">
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-gray-700">Current Password</Label>
                                    <div className="relative">
                                        <Input
                                            type={showCurrentPassword ? "text" : "password"}
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            required
                                            className="w-full h-12 px-4 pr-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900 placeholder-gray-500"
                                            placeholder="Enter current password"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 hover:bg-transparent"
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        >
                                            {showCurrentPassword ? <EyeOffIcon className="h-4 w-4 text-gray-400" /> : <EyeIcon className="h-4 w-4 text-gray-400" />}
                                        </Button>
                                    </div>
                                    {!currentPassword && error === '' && (
                                        <div className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
                                            Please fill out this field.
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-gray-700">New Password</Label>
                                    <div className="relative">
                                        <Input
                                            type={showNewPassword ? "text" : "password"}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            className="w-full h-12 px-4 pr-12 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900 placeholder-gray-500"
                                            placeholder="Enter new password"
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 hover:bg-transparent"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                        >
                                            {showNewPassword ? <EyeOffIcon className="h-4 w-4 text-gray-400" /> : <EyeIcon className="h-4 w-4 text-gray-400" />}
                                        </Button>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-gray-700">Confirm New Password</Label>
                                    <Input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        required
                                        className="w-full h-12 px-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-gray-900 placeholder-gray-500"
                                        placeholder="Confirm new password"
                                    />
                                </div>

                                {error && <p className="text-red-500 text-sm font-medium">{error}</p>}

                                <Button 
                                    type="submit" 
                                    className="w-full h-12 bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-lg font-medium hover:from-blue-600 hover:to-cyan-500 transition-all duration-200 disabled:opacity-50 text-base"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            <span>Changing Password...</span>
                                        </div>
                                    ) : (
                                        'Change Password'
                                    )}
                                </Button>
                            </form>
                        )}

                        {activeTab === 'email' && (
                            <form onSubmit={showOtpInput ? handleEmailChange : handleEmailVerification} className="space-y-5">
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium text-gray-700">New Email Address</Label>
                                    <Input
                                        type="email"
                                        value={newEmail}
                                        onChange={(e) => setNewEmail(e.target.value)}
                                        required
                                        disabled={showOtpInput}
                                        className="w-full h-12 px-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 disabled:bg-gray-100 text-gray-900 placeholder-gray-500"
                                        placeholder="Enter new email address"
                                    />
                                </div>

                                {showOtpInput && (
                                    <div className="space-y-2">
                                        <Label className="text-sm font-medium text-gray-700">Enter OTP</Label>
                                        <Input
                                            type="text"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            required
                                            maxLength={6}
                                            placeholder="Enter 6-digit OTP"
                                            className="w-full h-12 px-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 text-center text-lg tracking-wider text-gray-900 placeholder-gray-500"
                                        />
                                        <div className="mt-2 text-center">
                                            {!isResendActive ? (
                                                <p className="text-gray-500">
                                                    Resend OTP in: <strong>{formatTime(otpTime)}</strong>
                                                </p>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={handleResendOtp}
                                                    disabled={resentOtp}
                                                    className={`text-sm font-medium transition-colors underline-offset-2 
                                                        ${resentOtp ? "text-gray-400 cursor-not-allowed" : "text-blue-600 hover:text-blue-700 underline"}`}
                                                >
                                                    {resentOtp ? "OTP Sent" : "Resend OTP"}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {error && <p className="text-red-500 text-sm font-medium">{error}</p>}

                                <Button 
                                    type="submit" 
                                    className="w-full h-12 bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-lg font-medium hover:from-blue-600 hover:to-cyan-500 transition-all duration-200 disabled:opacity-50 text-base"
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <div className="flex items-center justify-center">
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            <span>{showOtpInput ? 'Verifying...' : 'Sending OTP...'}</span>
                                        </div>
                                    ) : (
                                        showOtpInput ? 'Change Email' : 'Send Verification Code'
                                    )}
                                </Button>
                            </form>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default SettingsModal;